import { ArrowDownIcon } from "@kds-web/icons";
import clsx from "clsx";
import React, { useState } from "react";
import type { JSX } from "react/jsx-runtime";
import DropdownList from "./DropdownList";

type Props = {
  onFilterSelect: (filter: DropdownFilter) => void;
  defaultFilter: string;
  filters: DropdownFilter[];
  listId: string;
  label: string;
  disabled?: boolean;
};

export interface DropdownFilter {
  value: number;
  name: string;
  children?: DropdownFilter[];
}

function Dropdown({
  filters,
  onFilterSelect,
  listId,
  label,
  defaultFilter,
  disabled,
}: Props): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [parentActiveIndex, setParentActiveIndex] = useState(-1);
  const [childActiveIndex, setChildActiveIndex] = useState(-1);

  function handleArrowDown(): void {
    if (!isOpen) {
      setIsOpen(true);
    } else if (childActiveIndex < 0) {
      setParentActiveIndex((prevIndex) => (prevIndex + 1) % filters.length);
    } else {
      const children = filters[parentActiveIndex]?.children;
      if (children && children.length > 0) {
        setChildActiveIndex((prevIndex) => (prevIndex + 1) % children.length);
      }
    }
  }

  function handleArrowUp(): void {
    if (isOpen) {
      if (childActiveIndex < 0) {
        setParentActiveIndex(
          (prevIndex) => (prevIndex + filters.length - 1) % filters.length,
        );
      } else {
        const children = filters[parentActiveIndex]?.children;
        if (children && children.length > 0) {
          setChildActiveIndex(
            (prevIndex) => (prevIndex + children.length - 1) % children.length,
          );
        }
      }
    }
  }

  function handleArrowRight(): void {
    if (filters[parentActiveIndex]?.children) {
      setChildActiveIndex(0);
    }
  }

  function handleArrowLeft(): void {
    setChildActiveIndex(-1);
  }

  function handleEnter(): void {
    if (!isOpen) {
      setIsOpen(true);
      return;
    }
    if (isOpen && parentActiveIndex >= 0) {
      const selectedFilter =
        childActiveIndex >= 0 && filters[parentActiveIndex].children
          ? filters[parentActiveIndex].children[childActiveIndex]
          : filters[parentActiveIndex];
      onFilterSelect(selectedFilter);
      setIsOpen(false);
    }
  }

  function handleKeyDown(
    e: KeyboardEvent | React.KeyboardEvent<HTMLDivElement>,
  ): void {
    if (disabled) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        handleArrowDown();
        break;
      case "ArrowUp":
        e.preventDefault();
        handleArrowUp();
        break;
      case "ArrowRight":
        e.preventDefault();
        handleArrowRight();
        break;
      case "ArrowLeft":
        e.preventDefault();
        handleArrowLeft();
        break;
      case "Enter":
      case "Space":
        handleEnter();
        break;
      case "Escape":
        resetDropdown();
        break;
      default:
        break;
    }
  }

  function handleFilterSelect(suggestion: DropdownFilter): void {
    setIsOpen(false);
    onFilterSelect(suggestion);
  }

  function resetDropdown(): void {
    setIsOpen(false);
    setParentActiveIndex(-1);
    setChildActiveIndex(-1);
  }

  function getCurrentLabel(): string {
    const isFilterHighlighted = filters[parentActiveIndex];
    if (!isFilterHighlighted) return defaultFilter;
    const isChildHighlighted =
      filters[parentActiveIndex].children &&
      filters[parentActiveIndex].children[childActiveIndex];
    if (filters[parentActiveIndex].children && isChildHighlighted) {
      return filters[parentActiveIndex].children[childActiveIndex].name;
    }
    return filters[parentActiveIndex].name;
  }

  function getActiveDescendant(): string {
    if (childActiveIndex >= 0) {
      return `${listId}-${parentActiveIndex}-${childActiveIndex}`;
    }
    if (parentActiveIndex >= 0) {
      return `${listId}-${parentActiveIndex}`;
    }
    return "";
  }

  return (
    <div className="relative flex size-full items-center bg-background text-onSurfaceNonessential">
      <div
        aria-label={label}
        className={clsx(
          "relative flex size-full max-h-xxxlarge items-center justify-start gap-small whitespace-nowrap px-medium py-small",
          isOpen ? "text-onSurface" : "text-onSurfaceNonessential",
          disabled ? "cursor-default opacity-medium" : "hover:text-onSurface",
          "focus-visible:outline-none focus-visible:ring-0",
        )}
        aria-haspopup="listbox"
        aria-controls={isOpen ? listId : undefined}
        aria-disabled={disabled}
        role="combobox"
        aria-expanded={isOpen}
        aria-activedescendant={getActiveDescendant()}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={(e) => !disabled && handleKeyDown(e)}
        onBlur={() => !disabled && resetDropdown()}
        tabIndex={disabled ? -1 : 0}
      >
        <span className="truncate">{getCurrentLabel()}</span>
        <ArrowDownIcon size="small" />
      </div>
      {isOpen && !disabled && (
        <DropdownList
          filters={filters}
          childActiveIndex={childActiveIndex}
          onFilterSelect={handleFilterSelect}
          parentActiveIndex={parentActiveIndex}
          listId={listId}
        />
      )}
    </div>
  );
}

export default Dropdown;
